import React, { useState, useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Box, Typography } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Container } from '@mui/material';
import SelectedNewsfeedCard from './SelectedNewsfeedCard';
import SelectedNewsfeedCheerUpBanner from './SelectedNewsfeedCheerUpBanner';
import NativeOrWeb from '../../../utils/native';
import { newsAnchorId } from './newsAnchor';

const useStyles = makeStyles()(theme => ({
  '@keyframes gradientShift': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' }
  },
  root: {
    minHeight: '100vh',
    backgroundColor: '#ffffff',
    paddingBottom: '80px'
  },
  // text base style
  '@global': {
    '.MuiTypography-root': {
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap'
    }
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    padding: '1rem',
    paddingBottom: '0.5rem',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    borderBottom: '1px solid #f0f3f4'
  },
  headerTitle: {
    color: '#111618',
    fontSize: '1.75rem',
    fontWeight: 700,
    flex: 1,
    textAlign: 'center'
  },
  topBanner: {
    // background: 'linear-gradient(135deg, , #ff6600)',
    background: '#6e27c5',
    backgroundSize: '150% 150%',
    borderRadius: '1rem',
    backgroundPosition: 'center',
    padding: '1.5rem',
    margin: '1rem 0',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)',
    textAlign: 'center'
  },
  topBannerText: {
    color: '#ffffff',
    fontSize: '1.875rem',
    fontWeight: 700,
    lineHeight: 1.4,
    textShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  topBannerIcon: {
    fontSize: '2rem',
    marginBottom: '0.5rem'
  },
  topBannerReplacement: {
    height: '1rem'
  },
  sectionTitle: {
    color: '#111618',
    fontSize: '1.625rem',
    fontWeight: 700,
    letterSpacing: '-0.015em',
    padding: '1.25rem 1rem 0.75rem 1rem'
  },
  latestNewsItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    backgroundColor: '#ffffff',
    padding: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#f8f9fa'
    }
  },
  latestNewsIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '0.5rem',
    backgroundColor: '#f0f3f4',
    flexShrink: 0,
    width: '3rem',
    height: '3rem'
  },
  latestNewsTitle: {
    color: '#111618',
    fontSize: '1.5rem',
    fontWeight: 500,
    lineHeight: 'normal',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  latestNewsSubtitle: {
    color: '#617c89',
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 'normal',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  listTile: {
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    padding: '1rem',
    '&:hover': {
      backgroundColor: '#f8f9fa'
    }
  },
  listItemAvatar: {
    minWidth: '64px'
  },
  newsAvatar: {
    width: '56px',
    height: '56px',
    borderRadius: '0.5rem'
  },
  listItemText: {
    '& .MuiListItemText-primary': {
      color: '#111618',
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.4,
      marginBottom: '0.25rem',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden'
    },
    '& .MuiListItemText-secondary': {
      color: '#617c89',
      fontSize: '1rem',
      fontWeight: 400
    }
  },
  keywordsContainer: {
    display: 'flex',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    flexWrap: 'wrap'
  },
  keywordChip: {
    height: '2rem',
    backgroundColor: '#f0f3f4',
    '& .MuiChip-label': {
      color: '#111618',
      fontSize: '1rem',
      fontWeight: 500
    }
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    fontSize: '1.5rem',
    color: '#617c89'
  }
}));

const SelectedNewsfeedsView = props => {
  const { intl, teams, userDetails, teamCampaignCodes } = props;
  const { classes } = useStyles();
  const [newsfeedList, setNewsfeedList] = useState(null);
  const [latestNewsfeed, setLatestNewsfeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id } = props.match.params;

  const host = process.env.HEADLESS_CMS_URL || '';
  const latestUrl = `${host}/api/selected-newsfeeds?sort=createdAt:desc&pagination[limit]=1&populate[news][populate]=*`;
  const listUrl = `${host}/api/selected-newsfeeds?pagination[limit]=10&sort=createdAt:desc&populate[news][populate]=*`;

  const campaignCodes = [
    teamCampaignCodes?.map(campaignCode => campaignCode.text),
    userDetails?.campaignCodes?.map(campaignCode => campaignCode.text)
  ].flat();
  const userTeams =
    teams?.reduce((acc, team) => {
      acc.push(team.teamShortName);
      acc.push(team._id);
      acc.push(team.teamName);
      return acc;
    }, []) || [];

  useEffect(() => {
    setLoading(true);

    // If we have an ID, show detail view
    // Otherwise, showPromise.all
    Promise.all([fetch(latestUrl), fetch(listUrl)])
      .then(async ([latest, list]) => {
        return Promise.all([latest.json(), list.json()]);
      })
      .then(([latest, newsHistory]) => {
        if (latest?.data && latest.data.length > 0) {
          setLatestNewsfeed(latest.data[0]);
        }
        if (newsHistory?.data && newsHistory.data.length > 0) {
          setNewsfeedList(newsHistory.data);
        }
      })
      .catch(err => console.error('Error fetching newsfeeds:', err))
      .finally(() => setLoading(false));
  }, [id, host, latestUrl]);

  if (loading) {
    return (
      <Box className={classes.loading}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!newsfeedList || newsfeedList.length === 0) {
    return (
      <Box className={classes.loading}>
        <Typography>No newsfeed found</Typography>
      </Box>
    );
  }

  const getTopBanner = (top_banner, campaignCode, teams) => {
    if (!top_banner || !campaignCode || (campaignCode.length === 0 && !teams) || teams.length === 0) return undefined;

    // get the first team that matches the top_banner.teams
    const topBannerTeam = teams?.find(team => top_banner?.teams?.[team]);
    if (topBannerTeam) {
      return top_banner?.teams?.[topBannerTeam];
    }

    // get the first campaign code that matches the top_banner.campaign_codes
    const campaignCodeFound = campaignCode?.find(code => top_banner?.campaign_codes?.[code]);
    if (campaignCodeFound) {
      return top_banner?.campaign_codes?.[campaignCodeFound];
    }

    return top_banner?.default;
  };
  const topBanner = getTopBanner(latestNewsfeed.attributes.top_banner, campaignCodes, userTeams);
  const featuredNews = newsfeedList.filter(newsfeed => newsfeed.id !== latestNewsfeed.id);
  const cheerUp = latestNewsfeed.attributes.cheer_up;

  const notificationPermissionCheck = async () => {
    const isNative = NativeOrWeb.isNativePlatform();
    if (isNative) {
      NativeOrWeb.checkAndRequestPermissions(false, 'selectedNewsfeedPermissionsRequested', 1 / 24);
    }
    return isNative;
  };

  const openNewsItem = (newsfeed, newsItem, newsIndex) => {
    notificationPermissionCheck();
    const anchor = newsAnchorId(newsItem, newsIndex);
    props.history.push({
      pathname: `/selected_newsfeed/${newsfeed.id}`,
      hash: `#${anchor}`,
      state: { news: newsfeed }
    });
  };

  return (
    <Container
      maxWidth="md"
      style={{
        padding: '46px 16px 80px 16px',
        height: 'auto'
      }}
    >
      <Box className={classes.root}>
        {/* Top Banner */}
        {topBanner ? (
          <Box className={classes.topBanner}>
            <Typography className={classes.topBannerText}>{topBanner}</Typography>
          </Box>
        ) : (
          <Box className={classes.topBannerReplacement} />
        )}
        {/* Cheer Up Banner */}
        <SelectedNewsfeedCheerUpBanner message={cheerUp} />
        {/* 
        <Card className={classes.featuredCard}>
          本周見客熱話
          <Typography className={classes.sectionTitle}>今日熱門新聞</Typography>
          {news.map((newsItem, index) => (
            <Box key={index} className={classes.latestNewsItem} onClick={() => handleLatestNewsClick()}>
              <Box className={classes.latestNewsIcon}>
                <Typography style={{ fontSize: '1.5rem' }}>{index === 0 ? '💥' : index === 1 ? '📈' : '💬'}</Typography>
              </Box>
              <Box flex={1}>
                <Typography className={classes.latestNewsTitle}>{newsItem.title}</Typography>
                <Typography className={classes.latestNewsSubtitle}>
                  {formatDate(newsItem.timestamp)} • {newsItem.source}
                </Typography>
              </Box>
              <ArrowForwardIosIcon style={{ fontSize: '1rem', color: '#617c89' }} />
            </Box>
          ))} 
        </Card> */}
        <Typography className={classes.sectionTitle} style={{ color: '#3966f8' }}>
          {intl.formatMessage({ id: 'selected-newsfeed-title' })}
        </Typography>
        <SelectedNewsfeedCard
          intl={intl}
          history={props.history}
          key={'latestNewsfeed'}
          // title={intl.formatMessage({ id: 'selected-newsfeed-title' })}
          selectedNewsfeed={latestNewsfeed}
          onOpenNewsItem={(newsItem, newsIndex) => openNewsItem(latestNewsfeed, newsItem, newsIndex)}
        />

        {/* Past Newsfeeds */}
        <Typography className={classes.sectionTitle}>
          {intl.formatMessage({ id: 'selected-newsfeed-past-title' })}
        </Typography>
        {featuredNews && featuredNews.length > 0 && (
          <Box>
            {featuredNews
              .map(selectedNewsfeed => (
                <SelectedNewsfeedCard
                  intl={intl}
                  history={props.history}
                  key={selectedNewsfeed.id}
                  selectedNewsfeed={selectedNewsfeed}
                  onOpenNewsItem={(newsItem, newsIndex) => openNewsItem(selectedNewsfeed, newsItem, newsIndex)}
                />
              ))
              .flat()}
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default SelectedNewsfeedsView;
